// src/utils/auth.ts

const TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'authUser';
const SESSION_TIMESTAMP_KEY = 'sessionTimestamp';
const SESSION_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

export const getAuthToken = (): string | null => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    
    // Check if session has expired
    const timestamp = localStorage.getItem(SESSION_TIMESTAMP_KEY);
    if (timestamp) {
      const sessionAge = Date.now() - parseInt(timestamp);
      if (sessionAge > SESSION_DURATION) {
        console.log('Session expired, clearing auth data');
        clearAuthData();
        return null;
      }
    }
    
    return token;
  } catch (error: any) {
    console.error('Error getting auth token:', error);
    // Don't clear data on localStorage errors, just return null
    // This prevents issues during page refresh when localStorage might not be fully available
    return null;
  }
};

export const setAuthToken = (token: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());
  } catch (error: any) {
    console.error('Error setting auth token:', error);
  }
};

export const removeAuthToken = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(SESSION_TIMESTAMP_KEY);
  } catch (error: any) {
    console.error('Error removing auth token:', error);
  }
};

export const getRefreshToken = (): string | null => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (error: any) {
    console.error('Error getting refresh token:', error);
    return null;
  }
};

export const setRefreshToken = (token: string): void => {
  try {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch (error: any) {
    console.error('Error setting refresh token:', error);
  }
};

export const removeRefreshToken = (): void => {
  try {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (error: any) {
    console.error('Error removing refresh token:', error);
  }
};

export const getUser = (): any => {
  try {
    const userStr = localStorage.getItem(USER_KEY);
    if (userStr) {
      return JSON.parse(userStr);
    }
  } catch (error: any) {
    console.error('Error parsing user from localStorage:', error);
    // Clear corrupted user data
    removeUser();
  }
  return null;
};

export const setUser = (user: any): void => {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch (error: any) {
    console.error('Error setting user:', error);
  }
};

export const removeUser = (): void => {
  try {
    localStorage.removeItem(USER_KEY);
  } catch (error: any) {
    console.error('Error removing user:', error);
  }
};

export const isAuthenticated = (): boolean => {
  try {
    const token = getAuthToken();
    const user = getUser();
    return !!(token && user);
  } catch (error: any) {
    console.error('Error checking authentication:', error);
    return false;
  }
};

// Check if session is valid locally without network calls
export const isSessionValidLocally = (): boolean => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const user = localStorage.getItem(USER_KEY);
    const timestamp = localStorage.getItem(SESSION_TIMESTAMP_KEY);
    
    if (!token || !user) return false;
    
    // Check if session has expired
    if (timestamp) {
      const sessionAge = Date.now() - parseInt(timestamp);
      if (sessionAge > SESSION_DURATION) {
        return false;
      }
    }
    
    return true;
  } catch (error: any) {
    console.error('Error checking local session validity:', error);
    return false;
  }
};

export const clearAuthData = (): void => {
  try {
    removeAuthToken();
    removeRefreshToken();
    removeUser();
    localStorage.removeItem(SESSION_TIMESTAMP_KEY);
  } catch (error: any) {
    console.error('Error clearing auth data:', error);
  }
};

// Safe token validation - only clear if explicitly invalid
export const validateTokenSafely = async (token: string): Promise<boolean> => {
  try {
    const API_URL = process.env.REACT_APP_API_URL || 'https://lavernaibackend.onrender.com/api';
    const controller = new AbortController();
    
    // Set a timeout for the request
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    const response = await fetch(`${API_URL}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    // Only return false for explicit auth failures
    return response.status !== 401 && response.status !== 403;
  } catch (error: any) {
    // Network errors, timeouts, and other errors should not invalidate tokens
    if (error.name === 'AbortError') {
      console.debug('Token validation timeout (ignored)');
    } else {
      console.debug('Token validation network error (ignored):', error);
    }
    return true;
  }
};

// Check if session is about to expire (within 1 hour)
export const isSessionExpiringSoon = (): boolean => {
  try {
    const timestamp = localStorage.getItem(SESSION_TIMESTAMP_KEY);
    if (!timestamp) return false;
    
    const sessionAge = Date.now() - parseInt(timestamp);
    const oneHour = 60 * 60 * 1000;
    
    return sessionAge > (SESSION_DURATION - oneHour);
  } catch (error: any) {
    console.error('Error checking session expiration:', error);
    return false;
  }
};

// Extend session timestamp (useful for keeping users logged in during activity)
export const extendSession = (): void => {
  try {
    localStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());
  } catch (error: any) {
    console.error('Error extending session:', error);
  }
}; 

// Test authentication flow and help debug issues
export const testAuthFlow = (): void => {
  try {
    console.log('=== Authentication Flow Test ===');
    
    const token = getAuthToken();
    const user = getUser();
    const refreshToken = getRefreshToken();
    const timestamp = localStorage.getItem(SESSION_TIMESTAMP_KEY);
    
    console.log('Current auth state:', {
      hasToken: !!token,
      hasUser: !!user,
      hasRefreshToken: !!refreshToken,
      hasTimestamp: !!timestamp,
      tokenLength: token?.length || 0,
      userKeys: user ? Object.keys(user) : [],
      sessionAge: timestamp ? Date.now() - parseInt(timestamp) : null,
      sessionValid: isSessionValidLocally()
    });
    
    if (token && user) {
      console.log('✅ Session appears to be valid locally');
    } else {
      console.log('❌ No valid session found locally');
    }
  } catch (error: any) {
    console.error('Error testing auth flow:', error);
  }
}; 

// Robust session restoration for page refresh scenarios
export const restoreSession = async (): Promise<{ token: string | null; user: any; refreshToken: string | null } | null> => {
  try {
    console.log('=== Starting session restoration ===');
    
    // Try multiple times to get localStorage data in case of timing issues
    let token = localStorage.getItem(TOKEN_KEY);
    let user = localStorage.getItem(USER_KEY);
    let refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    
    console.log('First attempt at session restoration:', {
      hasToken: !!token,
      hasUser: !!user,
      hasRefreshToken: !!refreshToken,
      tokenLength: token?.length || 0,
      userLength: user?.length || 0
    });
    
    // If first attempt failed, try again after a short delay
    if (!token || !user) {
      console.log('First attempt failed, retrying after delay...');
      await new Promise(resolve => setTimeout(resolve, 50)); // Reduced delay
      token = localStorage.getItem(TOKEN_KEY);
      user = localStorage.getItem(USER_KEY);
      refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      
      console.log('Second attempt at session restoration:', {
        hasToken: !!token,
        hasUser: !!user,
        hasRefreshToken: !!refreshToken,
        tokenLength: token?.length || 0,
        userLength: user?.length || 0
      });
      
      // If still no data, try one more time with a longer delay
      if (!token || !user) {
        console.log('Second attempt failed, trying one more time...');
        await new Promise(resolve => setTimeout(resolve, 100)); // Reduced delay
        token = localStorage.getItem(TOKEN_KEY);
        user = localStorage.getItem(USER_KEY);
        refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        
        console.log('Third attempt at session restoration:', {
          hasToken: !!token,
          hasUser: !!user,
          hasRefreshToken: !!refreshToken,
          tokenLength: token?.length || 0,
          userLength: user?.length || 0
        });
      }
    }
    
    if (token && user) {
      try {
        const parsedUser = JSON.parse(user);
        const timestamp = localStorage.getItem(SESSION_TIMESTAMP_KEY);
        
        console.log('Session data found:', {
          userKeys: Object.keys(parsedUser),
          hasTimestamp: !!timestamp,
          timestamp: timestamp
        });
        
        // Check if session has expired
        if (timestamp) {
          const sessionAge = Date.now() - parseInt(timestamp);
          const sessionAgeMinutes = Math.floor(sessionAge / (1000 * 60));
          console.log(`Session age: ${sessionAgeMinutes} minutes`);
          
          if (sessionAge > SESSION_DURATION) {
            console.log('Session expired during restoration, clearing auth data');
            clearAuthData();
            return null;
          }
        } else {
          // If no timestamp exists, create one to ensure session validity
          console.log('No session timestamp found, creating one now');
          localStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());
        }
        
        // Validate that the user object has required fields
        if (!parsedUser.id && !parsedUser.userId) {
          console.warn('User object missing required ID field, clearing invalid session');
          clearAuthData();
          return null;
        }
        
        console.log('Session restoration successful');
        return {
          token,
          user: parsedUser,
          refreshToken
        };
      } catch (parseError) {
        console.error('Error parsing user during session restoration:', parseError);
        // Clear corrupted user data
        clearAuthData();
        return null;
      }
    } else {
      console.log('No valid session data found in localStorage after multiple attempts');
      return null;
    }
  } catch (error: any) {
    console.error('Error during session restoration:', error);
    return null;
  }
}; 

// Global debug function that can be called from browser console
export const debugAuthState = (): void => {
  try {
    console.log('=== Authentication State Debug ===');
    
    const token = localStorage.getItem(TOKEN_KEY);
    const user = localStorage.getItem(USER_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const timestamp = localStorage.getItem(SESSION_TIMESTAMP_KEY);
    
    console.log('LocalStorage state:', {
      hasToken: !!token,
      hasUser: !!user,
      hasRefreshToken: !!refreshToken,
      hasTimestamp: !!timestamp,
      tokenLength: token?.length || 0,
      userLength: user?.length || 0
    });
    
    if (user) {
      try {
        const parsedUser = JSON.parse(user);
        console.log('User data:', {
          keys: Object.keys(parsedUser),
          firstName: parsedUser.firstName,
          email: parsedUser.email,
          role: parsedUser.role
        });
      } catch (e) {
        console.log('User data parse error:', e);
      }
    }
    
    if (timestamp) {
      const sessionAge = Date.now() - parseInt(timestamp);
      const sessionAgeMinutes = Math.floor(sessionAge / (1000 * 60));
      const sessionAgeHours = Math.floor(sessionAge / (1000 * 60 * 60));
      console.log('Session timing:', {
        timestamp: timestamp,
        sessionAge: `${sessionAgeMinutes} minutes (${sessionAgeHours} hours)`,
        isValid: sessionAge <= SESSION_DURATION
      });
    }
    
    console.log('Session validity:', {
      isSessionValidLocally: isSessionValidLocally(),
      isSessionExpiringSoon: isSessionExpiringSoon()
    });
    
    // Test the restore function
    console.log('Testing restoreSession function...');
    restoreSession().then(result => {
      console.log('restoreSession result:', result);
    });
    
  } catch (error: any) {
    console.error('Error in debugAuthState:', error);
  }
};

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).debugAuthState = debugAuthState;
  console.log('Debug function available: window.debugAuthState()');
} 